import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import JoditEditor, { Jodit } from 'jodit-react';
import { sanitizeEmailHtml } from '../../../utils/emailSanitize';

// Visual editor for HTML-mode email nodes, powered by Jodit (MIT). Runs in iframe + full-document
// mode so the pasted email's doctype/head/styles are preserved; Jodit supplies the toolbar, table
// popup, link/image dialogs and the source view. config.body always holds the full HTML document.
const HtmlEmailEditor = forwardRef(({ html, sourceMode, showToolbar, onChange, onVarTrigger, onEscape }, ref) => {
  const editorRef = useRef(null);
  const debounceRef = useRef(null);
  const callbacksRef = useRef({});
  callbacksRef.current = { onChange, onVarTrigger, onEscape };

  const emitChange = () => {
    const editor = editorRef.current;
    // Reason: what we emit must equal editor.value exactly — if the html prop ever differs, jodit-react re-assigns it and
    // Jodit rewrites the whole iframe document, which collapses the selection to #document (toolbar actions then throw).
    if (editor) callbacksRef.current.onChange?.(editor.value);
  };

  const flushEmit = () => {
    if (!debounceRef.current) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = null;
    emitChange();
  };

  const handleChange = () => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(emitChange, 300);
  };

  // Runs once when Jodit initializes: wire the "{" variable trigger, Escape, and whole-document paste.
  const bindEditor = useCallback((editor) => {
    editorRef.current = editor;
    editor.e.on('keyup', (e) => {
      if (e.key === 'Escape') return callbacksRef.current.onEscape?.();
      if (e.key !== '{') return;
      const rect = editor.s.range.getBoundingClientRect();
      const frameRect = editor.iframe ? editor.iframe.getBoundingClientRect() : { top: 0, left: 0 };
      const container = editor.container.closest('.preview-body');
      const containerRect = container ? container.getBoundingClientRect() : { top: 0, left: 0 };
      callbacksRef.current.onVarTrigger?.({ top: frameRect.top - containerRect.top + rect.bottom + 6, left: frameRect.left - containerRect.left + rect.left });
    });
    // Reason: pasting a full HTML email into an empty editor should become the document, not get inserted as text.
    editor.e.on('paste', (e) => {
      const pastedText = Jodit.modules.Helpers.getDataTransfer(e)?.getData('text/plain') || '';
      if (!looksLikeHtmlEmail(pastedText)) return;
      if (editor.editor.textContent.trim() || editor.editor.querySelector('img,table')) return;
      editor.e.stopPropagation('paste');
      editor.value = pastedText;
      return false;
    }, { top: true });
    // Reason: the table-cell popup is for a click on a cell (or a multi-cell drag); once the user drags/shift-selects text it
    // must stay out of the way. selectionchange is used because Jodit clears the native range mid-drag and the browser
    // re-extends it a tick later, so mouseup alone misfires.
    const isTextSelected = () => !editor.s.isCollapsed() && editor.getInstance(Jodit.modules.Table, editor.o).getAllSelectedCells().length < 2;
    editor.e.on('showPopup', (elm, rect, type) => { if (type === 'cells' && isTextSelected()) { editor.e.stopPropagation('showPopup'); return false; } }, { top: true });
    editor.e.on(editor.ed, 'selectionchange', () => { if (isTextSelected()) editor.e.fire('hidePopup', 'cells'); });
    // Reason: the frame is its own document, so the app's brand scrollbar (App.css `html { scrollbar-color }`) doesn't
    // reach it — and Jodit rebuilds the document on every full-HTML value, so re-apply after each rebuild. It goes in
    // adoptedStyleSheets (never serialized) so it can't leak into editor.value / the saved email HTML.
    const injectScrollbarStyle = () => {
      const doc = editor.ed;
      if (!doc || doc.adoptedStyleSheets.length) return;
      const secondary = getComputedStyle(document.documentElement).getPropertyValue('--secondary-color').trim() || '#FBFBFF';
      const thumb = `color-mix(in srgb, #817fda 25%, ${secondary})`;
      const sheet = new editor.ew.CSSStyleSheet();
      sheet.replaceSync(`html{scrollbar-color:${thumb} ${secondary}}::-webkit-scrollbar{width:10px}::-webkit-scrollbar-track{background:${secondary}}::-webkit-scrollbar-thumb{background:${thumb};border-radius:14px}`);
      doc.adoptedStyleSheets = [sheet];
    };
    editor.e.on('prepareWYSIWYGEditor afterSetMode', injectScrollbarStyle);
    injectScrollbarStyle();
  }, []);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    const target = sourceMode ? Jodit.MODE_SOURCE : Jodit.MODE_WYSIWYG;
    if (editor.getMode() !== target) editor.setMode(target);
  }, [sourceMode]);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  useImperativeHandle(ref, () => ({
    insertText: (text, { deleteTrigger = false } = {}) => {
      const editor = editorRef.current;
      if (!editor) return;
      editor.s.focus();
      const range = editor.s.range;
      // Remove the "{" the user typed to open the dropdown before inserting the full variable
      if (deleteTrigger && range?.collapsed && range.startContainer.nodeType === 3 && range.startOffset > 0) {
        range.setStart(range.startContainer, range.startOffset - 1);
        range.deleteContents();
      }
      editor.s.insertHTML(editor.ed.createTextNode(text));
    },
    focus: () => editorRef.current?.s.focus(),
    flush: flushEmit
  }));

  const config = useMemo(() => JODIT_CONFIG, []);

  return (
    <div className={`seq-html-editor ${showToolbar ? 'show-toolbar' : 'hide-toolbar'}`}>
      <JoditEditor value={html || ''} config={config} editorRef={bindEditor} onChange={handleChange} />
    </div>
  );
});

// Read-only, fully sandboxed preview for HTML-mode email bodies (test drawer). Rendering is
// debounced so live typing elsewhere doesn't reload the iframe on every keystroke.
export const SanitizedEmailFrame = ({ html, className }) => {
  const [renderedHtml, setRenderedHtml] = useState(html);
  useEffect(() => {
    const timer = setTimeout(() => setRenderedHtml(html), 250);
    return () => clearTimeout(timer);
  }, [html]);
  return <iframe sandbox="" srcDoc={sanitizeEmailHtml(renderedHtml, { keepStyles: true })} className={`seq-html-preview-frame ${className || ''}`} title="Email preview" />;
};

// Matches pasted content that is a designed HTML email rather than typed rich text.
const PASTE_DETECT_REGEX = /<!DOCTYPE\s+html|<html[\s>]|<head[\s>]|<body[\s>]|<style[\s>]|<table[\s>]/i;
export const looksLikeHtmlEmail = (text) => PASTE_DETECT_REGEX.test(text || '');

export const isFullHtmlDocument = (html) => /<!DOCTYPE\s+html|<html[\s>]|<body[\s>]/i.test(html || '');

// Client-side twin of the server's appendToBody — keeps signature preview placement in sync with send.
export const appendToBodyClient = (html, fragment) => (html || '').includes('</body>') ? html.replace('</body>', `${fragment}</body>`) : `${html || ''}${fragment}`;

// Reason: iframeStyle is emptied so Jodit's default body padding/background can't alter the email's own design.
const JODIT_CONFIG = {
  iframe: true,
  editHTMLDocumentMode: true,
  iframeStyle: '',
  // Reason: matches .preview-body in regular mode so the HTML node fits the panel without an outer scrollbar.
  height: '40dvh',
  // Reason: the toolbar is always built and shown/hidden by CSS from the "A" text-options toggle, same as Quill's.
  toolbarAdaptive: false,
  buttons: ['bold', 'italic', 'underline', 'strikethrough', '|', 'font', 'fontsize', 'brush', 'paragraph', '|', 'ul', 'ol', 'align', '|', 'link', 'image'],
  popup: { cells: [...Jodit.defaultOptions.popup.cells, '|', 'table'] },
  // Reason: designed emails use empty inline elements as visual marks (logo dots, spacers); Jodit strips them as junk by default.
  cleanHTML: { removeEmptyElements: false },
  statusbar: false,
  showXPathInStatusbar: false,
  showCharsCounter: false,
  showWordsCounter: false,
  showPlaceholder: false,
  sourceEditor: 'area',
  askBeforePasteHTML: false,
  askBeforePasteFromWord: false,
  defaultActionOnPaste: 'insert_clear_html',
  // Reason: wrap-nodes wraps root-level whitespace text into <p>, which adds blank lines above/below a pasted design.
  // spellcheck stamps spellcheck="false" on <body>, which would make editor.value differ from the saved HTML (see emitChange).
  disablePlugins: 'powered-by-jodit,speech-recognize,ai-assistant,about,stat,xpath,wrap-nodes,spellcheck,resize-handler'
};

export default HtmlEmailEditor;
